import { type PaginationState } from '@tanstack/react-table'
import React, { type Dispatch, type FC, type SetStateAction } from 'react'

import { type CoverageAuditLogsFilters } from 'components/brokers/coverage_audit_logs/CoverageAuditLogsTable'
import {
  type MultipleSelectedValuesFilter,
  type SingleSelectedValueFilter,
} from 'components/brokers/types'
import EnterpriseFiltersView, { type FilterConfigurations } from 'components/reusable/filters/EnterpriseFiltersView'

import ActionsFilter from './filters/ActionsFilter'
import AfterDateFilter from './filters/AfterDateFilter'
import BeforeDateFilter from './filters/BeforeDateFilter'
import CorrectedByBrokerFilter from './filters/CorrectedByBrokerFilter'
import CreatedByBrokerFilter from './filters/CreatedByBrokerFilter'
import EnrollmentIdFilter from './filters/EnrollmentIdFilter'
import MembersFilter from './filters/MembersFilter'
import ReasonsFilter from './filters/ReasonsFilter'
import TargetIdFilter from './filters/TargetIdFilter'
import TargetTypesFilter from './filters/TargetTypesFilter'

export type CoverageAuditLogFilterGroupType = 'associations' | 'changes' | 'dates' | 'brokers'

// Filter configurations expected by SyncFiltersWithURL
export const filterConfigurations: FilterConfigurations<
  CoverageAuditLogsFilters,
  CoverageAuditLogFilterGroupType
> = {
  member_ids: {
    key: 'member_ids',
    label: 'Members',
    group: 'associations',
    isMultiValue: true,
  },
  enrollment_id: {
    key: 'enrollment_id',
    label: 'Enrollment ID',
    group: 'associations',
    isMultiValue: false,
  },
  reasons: {
    key: 'reasons',
    label: 'Reasons',
    group: 'changes',
    isMultiValue: true,
  },
  actions: {
    key: 'actions',
    label: 'Actions',
    group: 'changes',
    isMultiValue: true,
  },
  target_types: {
    key: 'target_types',
    label: 'Target Types',
    group: 'associations',
    isMultiValue: true,
  },
  target_id: {
    key: 'target_id',
    label: 'Target ID',
    group: 'associations',
    isMultiValue: false,
  },
  created_by_broker_id: {
    key: 'created_by_broker_id',
    label: 'Created By',
    group: 'brokers',
    isMultiValue: true,
  },
  corrected_by_broker_id: {
    key: 'corrected_by_broker_id',
    label: 'Corrected By',
    group: 'brokers',
    isMultiValue: true,
  },
  after_date: {
    key: 'after_date',
    label: 'After Date',
    group: 'dates',
    isMultiValue: false,
  },
  before_date: {
    key: 'before_date',
    label: 'Before Date',
    group: 'dates',
    isMultiValue: false,
  },
}

// Single select filter components
type SingleSelectFilterComponents = {
  after_date: FC<SingleSelectedValueFilter>
  before_date: FC<SingleSelectedValueFilter>
  enrollment_id: FC<SingleSelectedValueFilter>
  target_id: FC<SingleSelectedValueFilter>
}

const coverageAuditLogsSingleSelectFilters: SingleSelectFilterComponents = {
  after_date: AfterDateFilter,
  before_date: BeforeDateFilter,
  enrollment_id: EnrollmentIdFilter,
  target_id: TargetIdFilter,
}

// Multi select filter components
type MultiSelectFilterComponents = {
  reasons: FC<MultipleSelectedValuesFilter & { availableReasons?: string[] }>
  actions: FC<MultipleSelectedValuesFilter>
  member_ids: FC<MultipleSelectedValuesFilter>
  target_types: FC<MultipleSelectedValuesFilter>
  created_by_broker_id: FC<MultipleSelectedValuesFilter>
  corrected_by_broker_id: FC<MultipleSelectedValuesFilter>
}

const coverageAuditLogsMultiSelectFilters: MultiSelectFilterComponents = {
  reasons: ReasonsFilter,
  actions: ActionsFilter,
  member_ids: MembersFilter,
  target_types: TargetTypesFilter,
  created_by_broker_id: CreatedByBrokerFilter,
  corrected_by_broker_id: CorrectedByBrokerFilter,
}

export const getCoverageAuditLogsFiltersConfigurationSortValue = (
  filterConfiguration: { groupLabel: string }
): number => {
  switch (filterConfiguration.groupLabel) {
    case 'Associations':
      return 0
    case 'Brokers':
      return 1
    case 'Changes':
      return 2
    case 'Dates':
      return 3
    default:
      return 4
  }
}

interface CoverageAuditLogsFiltersViewProps {
  filters: CoverageAuditLogsFilters
  setFilters: Dispatch<SetStateAction<CoverageAuditLogsFilters>>
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
  availableReasons?: string[]
}

const CoverageAuditLogsFiltersView: React.FC<
  CoverageAuditLogsFiltersViewProps
> = ({
  filters,
  setFilters,
  pagination,
  setPagination,
  availableReasons,
}) => {
  // Pass availableReasons to the reasons filter via props
  const multiSelectFiltersWithProps = {
    ...coverageAuditLogsMultiSelectFilters,
    reasons: (props: MultipleSelectedValuesFilter) => (
      <ReasonsFilter {...props} availableReasons={availableReasons} />
    ),
  }

  return (
    <EnterpriseFiltersView
      filters={filters}
      setFilters={setFilters}
      pagination={pagination}
      setPagination={setPagination}
      filterConfigurations={filterConfigurations}
      singleSelectFilters={coverageAuditLogsSingleSelectFilters}
      multiSelectFilters={multiSelectFiltersWithProps}
      getFiltersConfigurationSortValue={getCoverageAuditLogsFiltersConfigurationSortValue}
    />
  )
}

export default CoverageAuditLogsFiltersView
